import { useMemo } from 'react';
import type { ActivitySubjectCard } from '@shared/types/activity-feed';
import { useTeam } from '@/lib/work-handoffs';
import { useFileRuns } from '@/lib/work-runs';
import { LivingWorkRow, type LivingWorkHandlers, type RowPlan } from './LivingWorkRow';
import { projectActivityLivingWorks } from './thread-living-model';
import { buildTodayRows } from './today-pipeline';
import { plannedRowNote, planTodayRows, type TodayBucket } from './today-horizons';
import { currentTitle, lastDoneLine } from './work-done';

const BUCKET_ORDER: Record<TodayBucket, number> = { today: 0, later: 1, someday: 2 };

/**
 * « À faire » on the page of an account (26/10/2026): the account's tasks as
 * Mon travail shows them, the same row and the same gestures (launch, plan,
 * pass on, set aside with its motif). The first cell says the state, since the
 * account is the page's title. What is planned for later follows; what is done
 * goes to the chain at the top of the page.
 */
export const AccountTaskList = ({
  subjects,
  accountSubjectId,
  ...handlers
}: LivingWorkHandlers & {
  /** Mon travail's read model (listActivitySubjects), already loaded by the accounts layout. */
  subjects: ActivitySubjectCard[];
  accountSubjectId: string;
}) => {
  const now = useMemo(() => new Date(), [subjects]);
  const planned = useMemo(() => planTodayRows(
    // The tasks of an app object whose account is this one join it (M5).
    buildTodayRows(projectActivityLivingWorks(subjects).filter(({ subject, topic }) =>
      subject.subjectId === accountSubjectId || subject.accountSubjectId === accountSubjectId
      || topic.accountSubjectId === accountSubjectId), []),
    now,
  ).sort((left, right) => BUCKET_ORDER[left.bucket] - BUCKET_ORDER[right.bucket]
    || (left.row.topic.plannedFor ?? '').localeCompare(right.row.topic.plannedFor ?? '')),
  [subjects, accountSubjectId, now]);
  const runs = useFileRuns(planned.map((item) => item.row.topic.id));
  const team = useTeam();
  const nameOf = (userId: string) => team?.members.find((member) => member.userId === userId)?.name ?? 'un collègue';

  if (planned.length === 0) return null;

  return (
    <section className="mb-8" aria-labelledby="account-tasks-title">
      <h2 id="account-tasks-title" className="mb-3 text-lg font-semibold text-domino-ink">À faire</h2>
      <div className="space-y-2 sm:space-y-0 sm:divide-y sm:divide-domino-cardBorder sm:overflow-visible sm:rounded-lg sm:border sm:border-domino-cardBorder sm:bg-domino-white">
        {planned.map((item) => {
          const { row } = item;
          if (row.kind !== 'account') return null;
          const last = runs.lastByTopic.get(row.topic.id);
          const title = currentTitle(row.topic, last);
          const plan: RowPlan = {
            commitment: item.commitment,
            bucket: item.bucket,
            plannable: item.plannable,
            note: plannedRowNote(item, now),
            title: title !== row.topic.title ? title : null,
            done: last ? { text: lastDoneLine(last, now, team?.me ?? null, nameOf), failed: last.status === 'failed' } : null,
          };
          return <LivingWorkRow key={row.key} item={row.item} plan={plan} identity="state" {...handlers} />;
        })}
      </div>
    </section>
  );
};
